<template>
  <div class="agenda-public-page">
    <div class="agenda-public-shell">
      <h1>Agende seu horário</h1>

      <div v-if="loading" class="agenda-public-state">
        <b-spinner small class="mr-2" />
        Carregando horários...
      </div>

      <div v-else-if="error" class="alert alert-light-danger">
        {{ error }}
      </div>

      <template v-else-if="confirmedSlot">
        <div class="agenda-public-confirmation">
          <strong>Horário agendado com sucesso.</strong>
          <span>
            {{ formatDate(confirmedSlot.inicio) }} das
            {{ formatTime(confirmedSlot.inicio) }} às
            {{ formatTime(confirmedSlot.fim) }}
          </span>
        </div>
        <div class="agenda-public-confirmation-footer">
          <img
            alt="Logo Nuts Tecnologia"
            :src="layoutConfig('self.logo.secundary')"
            class="agenda-public-logo"
          />
        </div>
      </template>

      <template v-else>
        <div class="agenda-public-meta">
          <span>{{ availableSlotsCount }} horários disponíveis</span>
          <span v-if="disabledSlotsCount">
            {{ disabledSlotsCount }} horários indisponíveis
          </span>
          <span v-if="link.servico">{{ link.servico.discriminacao }}</span>
          <span>{{ link.duracao_minutos }} min</span>
        </div>

        <div v-if="slots.length === 0" class="agenda-public-state">
          Nenhum horário disponível para este link.
        </div>

        <template v-else>
          <div v-if="availableDays.length > 1" class="agenda-public-day-picker">
            <button
              class="btn btn-light-primary agenda-public-day-arrow"
              type="button"
              title="Dia anterior"
              :disabled="!hasPreviousDay"
              @click="goToPreviousDay"
            >
              <ChevronLeft :size="18" />
            </button>

            <div class="agenda-public-days">
              <button
                v-for="day in availableDays"
                :key="day.key"
                class="agenda-public-day"
                :class="{ active: day.key === activeDayKey }"
                type="button"
                @click="changeDay(day.key)"
              >
                <CalendarCheck :size="18" />
                <span>{{ day.label }}</span>
                <small>{{ day.available }} horários</small>
              </button>
            </div>

            <button
              class="btn btn-light-primary agenda-public-day-arrow"
              type="button"
              title="Próximo dia"
              :disabled="!hasNextDay"
              @click="goToNextDay"
            >
              <ChevronRight :size="18" />
            </button>
          </div>

          <div v-if="activeDay" class="agenda-public-day-summary">
            <strong>{{ activeDay.weekday }}</strong>
            <span>{{ activeDay.longLabel }}</span>
          </div>

          <div v-if="activeDaySlots.length === 0" class="agenda-public-state">
            Nenhum horário disponível neste dia.
          </div>

          <div v-else class="agenda-public-slots">
            <button
              v-for="slot in activeDaySlots"
              :key="slot.inicio"
              type="button"
              class="agenda-public-slot"
              :class="{
                selected: selectedSlot && selectedSlot.inicio === slot.inicio,
                unavailable: isSlotPast(slot),
              }"
              :disabled="isSlotPast(slot)"
              @click="selectSlot(slot)"
            >
              <strong>
                {{ formatTime(slot.inicio) }} - {{ formatTime(slot.fim) }}
              </strong>
            </button>
          </div>
        </template>

        <div class="agenda-public-form">
          <b-form-group
            v-if="!link.id_pessoa"
            label="Código do cliente"
            label-for="id_pessoa"
          >
            <b-form-input id="id_pessoa" v-model="idPessoa" />
          </b-form-group>
          <b-form-group label="Observações">
            <b-form-textarea rows="3" v-model="observacoes" />
          </b-form-group>
          <div class="agenda-public-footer">
            <button
              type="button"
              class="btn btn-primary font-weight-bold"
              :disabled="!selectedSlot || saving"
              @click="confirmar"
            >
              {{ saving ? "Confirmando..." : "Confirmar horário" }}
            </button>
            <img
              alt="Logo"
              :src="layoutConfig('self.logo.secundary')"
              class="agenda-public-logo"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import CalendarCheck from "vue-material-design-icons/CalendarCheck.vue";
import ChevronLeft from "vue-material-design-icons/ChevronLeft.vue";
import ChevronRight from "vue-material-design-icons/ChevronRight.vue";

export default {
  name: "app-agendamento-publico",
  components: {
    CalendarCheck,
    ChevronLeft,
    ChevronRight,
  },
  data() {
    return {
      loading: true,
      saving: false,
      error: "",
      link: {},
      slots: [],
      selectedDay: "",
      selectedSlot: null,
      confirmedSlot: null,
      idPessoa: "",
      observacoes: "",
    };
  },
  computed: {
    ...mapGetters(["layoutConfig"]),
    disabledSlotsCount() {
      return this.slots.filter((slot) => this.isSlotPast(slot)).length;
    },
    availableSlotsCount() {
      return this.slots.length - this.disabledSlotsCount;
    },
    availableDays() {
      const days = [];
      const byDay = {};

      this.slots.forEach((slot) => {
        const key = this.slotDayKey(slot);

        if (!byDay[key]) {
          const date = moment(slot.inicio);
          byDay[key] = {
            key,
            label: date.format("DD/MM"),
            longLabel: date.format("DD [de] MMMM [de] YYYY"),
            weekday: this.capitalize(date.format("dddd")),
            total: 0,
            available: 0,
          };
          days.push(byDay[key]);
        }

        byDay[key].total += 1;

        if (!this.isSlotPast(slot)) {
          byDay[key].available += 1;
        }
      });

      return days.sort((a, b) => a.key.localeCompare(b.key));
    },
    activeDayKey() {
      if (
        this.selectedDay &&
        this.availableDays.some((day) => day.key === this.selectedDay)
      ) {
        return this.selectedDay;
      }

      const firstAvailable = this.availableDays.find((day) => day.available);

      return firstAvailable
        ? firstAvailable.key
        : this.availableDays.length
        ? this.availableDays[0].key
        : "";
    },
    activeDay() {
      return this.availableDays.find((day) => day.key === this.activeDayKey);
    },
    activeDayIndex() {
      return this.availableDays.findIndex(
        (day) => day.key === this.activeDayKey
      );
    },
    activeDaySlots() {
      if (!this.activeDayKey) {
        return [];
      }

      return this.slots.filter(
        (slot) => this.slotDayKey(slot) === this.activeDayKey
      );
    },
    hasPreviousDay() {
      return this.activeDayIndex > 0;
    },
    hasNextDay() {
      return (
        this.activeDayIndex >= 0 &&
        this.activeDayIndex < this.availableDays.length - 1
      );
    },
  },
  methods: {
    async loadHorarios() {
      if (this.confirmedSlot) return;

      this.loading = true;
      this.error = "";

      try {
        const { data } = await this.$http.get(
          `/agendamentos/publico/${this.$route.params.token}/horarios`
        );
        this.link = data.link || {};
        this.slots = data.slots || [];
        if (this.selectedSlot && this.isSlotPast(this.selectedSlot)) {
          this.selectedSlot = null;
        }
        this.ensureSelectedDay();
      } catch (error) {
        this.error =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível carregar este link.";
      } finally {
        this.loading = false;
      }
    },
    async confirmar() {
      if (!this.selectedSlot || this.isSlotPast(this.selectedSlot)) return;

      if (!this.link.id_pessoa && !this.idPessoa) {
        this.$toast.warning("Informe o código do cliente.");
        return;
      }

      this.saving = true;
      const slotConfirmado = { ...this.selectedSlot };

      try {
        await this.$http.post(
          `/agendamentos/publico/${this.$route.params.token}/confirmar`,
          {
            id_pessoa: this.link.id_pessoa || this.idPessoa,
            data_inicio: this.selectedSlot.inicio,
            observacoes: this.observacoes,
          }
        );

        this.confirmedSlot = slotConfirmado;
        this.selectedSlot = null;
        this.slots = [];
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
            error?.response?.data?.message ||
            "Não foi possível confirmar este horário."
        );
      } finally {
        this.saving = false;
      }
    },
    formatDate(value) {
      return moment(value).format("DD/MM/YYYY");
    },
    formatTime(value) {
      return moment(value).format("HH:mm");
    },
    slotDayKey(slot) {
      return moment(slot.inicio).format("YYYY-MM-DD");
    },
    isSlotPast(slot) {
      return moment(slot.inicio).isBefore(moment());
    },
    selectSlot(slot) {
      if (this.isSlotPast(slot)) return;
      this.selectedDay = this.slotDayKey(slot);
      this.selectedSlot = slot;
    },
    changeDay(dayKey) {
      if (this.selectedDay !== dayKey) {
        this.selectedSlot = null;
      }

      this.selectedDay = dayKey;
    },
    goToPreviousDay() {
      if (!this.hasPreviousDay) return;
      this.changeDay(this.availableDays[this.activeDayIndex - 1].key);
    },
    goToNextDay() {
      if (!this.hasNextDay) return;
      this.changeDay(this.availableDays[this.activeDayIndex + 1].key);
    },
    ensureSelectedDay() {
      if (!this.availableDays.length) {
        this.selectedDay = "";
        return;
      }

      if (
        this.selectedDay &&
        this.availableDays.some((day) => day.key === this.selectedDay)
      ) {
        return;
      }

      const firstAvailable = this.availableDays.find((day) => day.available);
      this.selectedDay = firstAvailable
        ? firstAvailable.key
        : this.availableDays[0].key;
    },
    capitalize(value) {
      if (!value) return "";
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  created() {
    moment.locale("pt-br");
    this.loadHorarios();
  },
};
</script>

<style scoped>
.agenda-public-page {
  min-height: 100vh;
  padding: 32px 16px;
  background: #f3f6f9;
}

.agenda-public-shell {
  max-width: 820px;
  margin: 0 auto;
  padding: 24px;
  border-radius: 6px;
  background: #fff;
}

.agenda-public-shell h1 {
  margin-bottom: 18px;
  color: #181c32;
  font-size: 28px;
  font-weight: 800;
}

.agenda-public-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.agenda-public-meta span {
  padding: 6px 10px;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 700;
}

.agenda-public-state {
  display: flex;
  align-items: center;
  min-height: 120px;
  color: #7e8299;
  font-weight: 700;
}

.agenda-public-confirmation {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 120px;
  padding: 20px;
  border: 1px solid #c9f7f5;
  border-radius: 6px;
  background: #f3fffe;
  color: #3f4254;
}

.agenda-public-confirmation strong {
  color: #181c32;
  font-size: 18px;
}

.agenda-public-confirmation span {
  color: #7e8299;
  font-weight: 700;
}

.agenda-public-confirmation-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.agenda-public-day-picker {
  align-items: stretch;
  display: grid;
  gap: 8px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  margin-bottom: 12px;
}

.agenda-public-day-arrow {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  min-height: 54px;
  min-width: 44px;
  padding: 0 10px;
}

.agenda-public-days {
  display: grid;
  gap: 8px;
  grid-auto-columns: minmax(130px, 1fr);
  grid-auto-flow: column;
  overflow-x: auto;
  padding-bottom: 2px;
}

.agenda-public-day {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #3f4254;
  display: grid;
  gap: 2px 8px;
  grid-template-columns: auto 1fr;
  justify-items: start;
  min-height: 54px;
  min-width: 0;
  padding: 8px 10px;
  text-align: left;
}

.agenda-public-day span,
.agenda-public-day small {
  min-width: 0;
  overflow-wrap: anywhere;
}

.agenda-public-day span {
  font-weight: 800;
}

.agenda-public-day small {
  color: #7e8299;
  font-size: 11px;
  font-weight: 700;
  grid-column: 2;
}

.agenda-public-day.active {
  background: #e1f0ff;
  border-color: #3699ff;
  color: #0b5cab;
}

.agenda-public-day.active small {
  color: #0b5cab;
}

.agenda-public-day-summary {
  align-items: center;
  background: #f6fbff;
  border: 1px solid #d8ecff;
  border-radius: 6px;
  color: #3f4254;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-bottom: 12px;
  padding: 10px 12px;
}

.agenda-public-day-summary strong {
  color: #181c32;
  font-weight: 800;
}

.agenda-public-day-summary span {
  color: #7e8299;
  font-weight: 700;
}

.agenda-public-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
}

.agenda-public-slot {
  min-height: 74px;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background: #fff;
  color: #3f4254;
  font-weight: 700;
}

.agenda-public-slot span {
  display: block;
  color: #7e8299;
}

.agenda-public-slot.selected {
  border-color: #3699ff;
  background: #e1f0ff;
}

.agenda-public-slot.unavailable {
  cursor: not-allowed;
  border-color: #e4e6ef;
  background: #f3f6f9;
  color: #b5b5c3;
}

.agenda-public-slot.unavailable span {
  color: #b5b5c3;
}

.agenda-public-form {
  border-top: 1px solid #ebedf3;
  padding-top: 18px;
}

.agenda-public-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.agenda-public-logo {
  max-height: 60px;
  max-width: 170px;
  object-fit: contain;
}

@media (max-width: 575.98px) {
  .agenda-public-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .agenda-public-day-picker {
    grid-template-columns: 42px minmax(0, 1fr) 42px;
  }

  .agenda-public-day {
    min-width: 126px;
  }
}
</style>
